import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../contexts/ToastContext';
import { sendgridApi } from '../api/sendgrid';
import {
  ChartBarIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatShortDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}`;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '-';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/**
 * Get the Monday-based ISO week start date for a given date string.
 */
function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday = 0
  d.setDate(d.getDate() - diff);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Group daily stats into weeks (Mon–Sun). Returns newest first.
 * Each week has: weekStart, weekEnd, days (sorted oldest first),
 * latest snapshot values, summed newSubscribers, and deltas from prior week.
 */
function groupByWeek(stats) {
  if (!stats.length) return [];

  // Stats come newest-first from API; reverse to oldest-first for grouping
  const sorted = [...stats].reverse();
  const weekMap = new Map();

  for (const day of sorted) {
    const ws = getWeekStart(day.date);
    if (!weekMap.has(ws)) {
      weekMap.set(ws, []);
    }
    weekMap.get(ws).push(day);
  }

  const weeks = [];
  for (const [weekStart, days] of weekMap) {
    const lastDay = days[days.length - 1]; // most recent day in the week
    const firstDay = days[0];

    // Week end is either Sunday or last available day
    const wsDate = new Date(weekStart + 'T00:00:00');
    wsDate.setDate(wsDate.getDate() + 6);
    const weekEndDate = `${wsDate.getFullYear()}-${String(wsDate.getMonth() + 1).padStart(2, '0')}-${String(wsDate.getDate()).padStart(2, '0')}`;
    const weekEnd = lastDay.date < weekEndDate ? lastDay.date : weekEndDate;

    const newSubscribers = days.reduce((sum, d) => sum + (d.newSubscribers || 0), 0);

    weeks.push({
      weekStart,
      weekEnd,
      days,
      // Use end-of-week snapshot for the summary row
      totalContacts: lastDay.totalContacts,
      subscribed: lastDay.subscribed,
      unsubscribed: lastDay.unsubscribed,
      bounced: lastDay.bounced,
      spamReported: lastDay.spamReported,
      newSubscribers,
      // Deltas computed below
      delta: {},
    });
  }

  // Compute week-over-week deltas (compare to previous week)
  for (let i = 1; i < weeks.length; i++) {
    const prev = weeks[i - 1];
    const curr = weeks[i];
    curr.delta = {
      totalContacts: curr.totalContacts - prev.totalContacts,
      subscribed: curr.subscribed - prev.subscribed,
      unsubscribed: curr.unsubscribed - prev.unsubscribed,
      bounced: curr.bounced - prev.bounced,
      spamReported: curr.spamReported - prev.spamReported,
      newSubscribers: curr.newSubscribers - prev.newSubscribers,
    };
  }

  // Return newest first
  return weeks.reverse();
}

function ChangeIndicator({ value, inverted = false }) {
  if (value == null) return null;
  if (value === 0) return <span className="inline-flex items-center text-xs mr-1.5 text-gray-300 dark:text-gray-500">&ndash;</span>;
  const isPositive = value > 0;
  // For unsub/bounced/spam, positive change is bad (inverted)
  const isGood = inverted ? !isPositive : isPositive;

  return (
    <span className={`inline-flex items-center text-xs mr-1.5 ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      {isPositive ? (
        <ArrowUpIcon className="h-3 w-3" />
      ) : (
        <ArrowDownIcon className="h-3 w-3" />
      )}
      {Math.abs(value).toLocaleString()}
    </span>
  );
}

function StatCard({ label, value, delta, subtitle, invertDelta = false, color }) {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-900 dark:bg-gray-900/30 dark:border-gray-800/50 dark:text-gray-100',
    green: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900/30 dark:border-green-800/50 dark:text-green-100',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-900/30 dark:border-yellow-800/50 dark:text-yellow-100',
    red: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/30 dark:border-red-800/50 dark:text-red-100',
    blue: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-100',
  };

  const deltaColor = delta == null || delta === 0
    ? 'text-gray-400 dark:text-gray-500'
    : (invertDelta ? delta < 0 : delta > 0)
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-500';

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color] || colorClasses.gray}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value != null ? value.toLocaleString() : '-'}</p>
      {delta != null && (
        <p className={`mt-1 text-xs font-medium flex items-center gap-0.5 ${deltaColor}`}>
          {delta === 0 ? (
            <span>&ndash; 7d</span>
          ) : (
            <>
              {delta > 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
              {Math.abs(delta).toLocaleString()} / 7d
            </>
          )}
        </p>
      )}
      {subtitle != null && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{subtitle.toLocaleString()} total</p>
      )}
    </div>
  );
}

function WeekRow({ week, isExpanded, onToggle }) {
  const hasDelta = Object.keys(week.delta).length > 0;

  return (
    <>
      <tr
        onClick={onToggle}
        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer select-none"
      >
        <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-1.5">
            <ChevronRightIcon
              className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
            />
            <span>{formatShortDate(week.weekStart)} – {formatShortDate(week.weekEnd)}</span>
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.totalContacts} />}
            {week.totalContacts?.toLocaleString()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-green-700 dark:text-green-300">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.subscribed} />}
            {week.subscribed?.toLocaleString()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-yellow-700 dark:text-yellow-300">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.unsubscribed} inverted />}
            {week.unsubscribed?.toLocaleString()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-red-700 dark:text-red-300">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.bounced} inverted />}
            {week.bounced?.toLocaleString()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.spamReported} inverted />}
            {week.spamReported?.toLocaleString()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300 font-medium">
          <div className="flex items-center justify-end">
            {hasDelta && <ChangeIndicator value={week.delta.newSubscribers} />}
            {week.newSubscribers?.toLocaleString()}
          </div>
        </td>
      </tr>

      {/* Expanded daily rows */}
      {isExpanded && week.days.slice().reverse().map((day) => (
        <tr key={day.date} className="bg-gray-50/60 dark:bg-gray-900/40">
          <td className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400 pl-10">{formatDate(day.date)}</td>
          <td className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400 text-right">{day.totalContacts?.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-xs text-green-600 dark:text-green-400 text-right">{day.subscribed?.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-xs text-yellow-600 dark:text-yellow-400 text-right">{day.unsubscribed?.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-xs text-red-600 dark:text-red-400 text-right">{day.bounced?.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400 text-right">{day.spamReported?.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-xs text-blue-600 dark:text-blue-400 text-right">{day.newSubscribers?.toLocaleString()}</td>
        </tr>
      ))}
    </>
  );
}

function ContactStatsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [computeDate, setComputeDate] = useState('');
  const [expandedWeeks, setExpandedWeeks] = useState({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['contactStats'],
    queryFn: () => sendgridApi.listContactStats({ limit: 500 }),
  });

  const computeMutation = useMutation({
    mutationFn: (date) => sendgridApi.computeContactStats(date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactStats'] });
      showSuccess('Contact stats computed successfully');
      setComputeDate('');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to compute contact stats');
    },
  });

  const handleCompute = () => {
    if (!computeDate) return;
    computeMutation.mutate(computeDate);
  };

  const toggleWeek = (weekStart) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [weekStart]: !prev[weekStart],
    }));
  };

  const stats = data?.stats || [];
  const latest = stats[0] || null;
  const sevenDaysAgo = stats.length >= 8 ? stats[7] : null;
  const delta7d = latest && sevenDaysAgo ? {
    totalContacts: latest.totalContacts - sevenDaysAgo.totalContacts,
    subscribed: latest.subscribed - sevenDaysAgo.subscribed,
    unsubscribed: latest.unsubscribed - sevenDaysAgo.unsubscribed,
    bounced: latest.bounced - sevenDaysAgo.bounced,
    newSubscribers: stats.slice(0, 7).reduce((sum, d) => sum + (d.newSubscribers || 0), 0),
  } : null;
  const weeks = groupByWeek(stats);

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Error loading contact stats: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ChartBarIcon className="h-7 w-7 text-gray-600 dark:text-gray-300" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contact Stats</h1>
        </div>

        {/* Compute controls */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={computeDate}
            onChange={(e) => setComputeDate(e.target.value)}
            className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
          />
          <button
            onClick={handleCompute}
            disabled={!computeDate || computeMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-obn-blue rounded-lg hover:bg-obn-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 ${computeMutation.isPending ? 'animate-spin' : ''}`} />
            Compute
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 dark:text-gray-500">Loading stats...</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard label="Total Contacts" value={latest?.totalContacts} delta={delta7d?.totalContacts} color="gray" />
            <StatCard label="Subscribed" value={latest?.subscribed} delta={delta7d?.subscribed} color="green" />
            <StatCard label="Unsub (7d)" value={delta7d?.unsubscribed} subtitle={latest?.unsubscribed} invertDelta color="yellow" />
            <StatCard label="Bounced (7d)" value={delta7d?.bounced} subtitle={latest?.bounced} invertDelta color="red" />
            <StatCard label="New (7d)" value={delta7d?.newSubscribers} color="blue" />
          </div>

          {/* Weekly Trend Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Week</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subscribed</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unsub</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bounced</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Spam</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">New</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {weeks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No stats data yet. Use the compute button to generate a snapshot.
                      </td>
                    </tr>
                  ) : (
                    weeks.map((week) => (
                      <WeekRow
                        key={week.weekStart}
                        week={week}
                        isExpanded={!!expandedWeeks[week.weekStart]}
                        onToggle={() => toggleWeek(week.weekStart)}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ContactStatsPage;
